import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Download, RefreshCw, Eye, MousePointerClick, ShoppingBag, CalendarCheck, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_META = {
  page_visit:         { label: 'Sayfa Ziyareti (QR)', icon: Eye,               accent: 'text-primary',   bg: 'bg-primary/10' },
  menu_click:         { label: 'Menüye Git',          icon: MousePointerClick, accent: 'text-amber-500', bg: 'bg-amber-500/10' },
  online_order_click: { label: 'Online Sipariş',      icon: ShoppingBag,       accent: 'text-green-500', bg: 'bg-green-500/10' },
  reservation_click:  { label: 'Rezervasyon',         icon: CalendarCheck,     accent: 'text-blue-400',  bg: 'bg-blue-400/10' },
  category_click:     { label: 'Kategori Tıklama',    icon: FolderOpen,        accent: 'text-violet-400', bg: 'bg-violet-400/10' },
  campaign_view:      { label: 'Kampanya Görüntüleme', icon: Eye,              accent: 'text-pink-400',  bg: 'bg-pink-400/10' },
  campaign_click:     { label: 'Kampanya Tıklama',    icon: MousePointerClick, accent: 'text-rose-500',  bg: 'bg-rose-500/10' },
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const AnalyticsPanel = ({ token }) => {
  const [from, setFrom] = useState(daysAgoStr(29));
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/analytics/summary?from_date=${from}&to_date=${to}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('summary fetch failed');
      setData(await res.json());
    } catch (e) {
      toast.error('İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [from, to, token]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const downloadCsv = async () => {
    try {
      const url = `${API_URL}/api/admin/analytics/export?from_date=${from}&to_date=${to}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `gomsu-analytics-${from}_to_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success('CSV indirildi');
    } catch (e) {
      toast.error('CSV indirilemedi');
    }
  };

  const setPreset = (days) => {
    setFrom(daysAgoStr(days - 1));
    setTo(todayStr());
  };

  const totals = data?.totals || {};
  const daily = data?.daily || [];
  const topCategories = data?.top_categories || [];
  const maxDay = Math.max(1, ...daily.map(d => Object.keys(EVENT_META).reduce((s, k) => s + (d[k] || 0), 0)));

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Date Range Controls */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Başlangıç:</span>
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="analytics-from-date" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Bitiş:</span>
            <Input type="date" value={to} min={from} max={todayStr()} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="analytics-to-date" />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { setFrom(todayStr()); setTo(todayStr()); }} data-testid="analytics-preset-today">Bugün</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset(7)} data-testid="analytics-preset-7">Son 7 gün</Button>
            <Button variant="outline" size="sm" onClick={() => setPreset(30)} data-testid="analytics-preset-30">Son 30 gün</Button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchSummary} data-testid="analytics-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Yenile
            </Button>
            <Button size="sm" onClick={downloadCsv} data-testid="analytics-csv-download">
              <Download className="w-4 h-4 mr-2" />CSV İndir
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Totals Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(EVENT_META).map(([key, meta]) => {
          const Icon = meta.icon;
          return (
            <Card key={key} className="overflow-hidden">
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-lg ${meta.bg} flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${meta.accent}`} />
                </div>
                <p className="text-xs text-muted-foreground mb-1">{meta.label}</p>
                <p className="text-2xl font-display text-foreground" data-testid={`analytics-total-${key}`}>
                  {(totals[key] || 0).toLocaleString('tr-TR')}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Daily Bar Chart (CSS) */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-medium text-foreground mb-4">Günlük Aktivite</h3>
          {daily.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Veri yok</p>
          ) : (
            <div className="flex items-end gap-1 h-48 overflow-x-auto pb-2">
              {daily.map((d) => {
                const stackKeys = ['page_visit', 'menu_click', 'online_order_click', 'reservation_click', 'category_click', 'campaign_view', 'campaign_click'];
                const total = stackKeys.reduce((s, k) => s + (d[k] || 0), 0);
                const heightPct = (total / maxDay) * 100;
                const label = d.date.slice(5); // MM-DD
                return (
                  <div key={d.date} className="flex flex-col items-center min-w-[36px] flex-1" title={`${d.date} · Toplam: ${total}`}>
                    <div className="w-full flex-1 flex flex-col justify-end">
                      <div className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden" style={{ height: `${heightPct}%` }}>
                        {stackKeys.map((k) => {
                          const v = d[k] || 0;
                          if (!v) return null;
                          const segHeight = (v / total) * 100;
                          const colorMap = {
                            page_visit:         'bg-primary',
                            menu_click:         'bg-amber-500',
                            online_order_click: 'bg-green-500',
                            reservation_click:  'bg-blue-400',
                            category_click:     'bg-violet-400',
                            campaign_view:      'bg-pink-400',
                            campaign_click:     'bg-rose-500',
                          };
                          return <div key={k} className={`${colorMap[k]}`} style={{ height: `${segHeight}%` }} />;
                        })}
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground mt-1 whitespace-nowrap">{label}</span>
                  </div>
                );
              })}
            </div>
          )}
          {/* Legend */}
          <div className="flex flex-wrap gap-3 mt-4 text-xs">
            {Object.entries(EVENT_META).map(([key, meta]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`inline-block w-3 h-3 rounded ${meta.bg.replace('/10', '')}`} />
                <span className="text-muted-foreground">{meta.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Daily Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">Tarih</th>
                  <th className="px-4 py-3 font-medium text-right">Ziyaret</th>
                  <th className="px-4 py-3 font-medium text-right">Menüye Git</th>
                  <th className="px-4 py-3 font-medium text-right">Online Sipariş</th>
                  <th className="px-4 py-3 font-medium text-right">Rezervasyon</th>
                  <th className="px-4 py-3 font-medium text-right">Kategori Tıklama</th>
                  <th className="px-4 py-3 font-medium text-right">Kampanya Görüntüleme</th>
                  <th className="px-4 py-3 font-medium text-right">Kampanya Tıklama</th>
                </tr>
              </thead>
              <tbody>
                {daily.slice().reverse().map((d) => (
                  <tr key={d.date} className="border-t border-border/40">
                    <td className="px-4 py-2">{d.date}</td>
                    <td className="px-4 py-2 text-right">{d.page_visit}</td>
                    <td className="px-4 py-2 text-right">{d.menu_click}</td>
                    <td className="px-4 py-2 text-right">{d.online_order_click}</td>
                    <td className="px-4 py-2 text-right">{d.reservation_click}</td>
                    <td className="px-4 py-2 text-right">{d.category_click}</td>
                    <td className="px-4 py-2 text-right">{d.campaign_view}</td>
                    <td className="px-4 py-2 text-right">{d.campaign_click}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Categories */}
      {topCategories.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-3">En Çok Tıklanan Kategoriler</h3>
            <div className="space-y-2">
              {topCategories.map((c, idx) => (
                <div key={c.category_id} className="flex items-center gap-3 text-sm">
                  <span className="w-6 text-muted-foreground">{idx + 1}.</span>
                  <span className="flex-1 capitalize">{c.category_id.replace(/-/g, ' ')}</span>
                  <span className="font-display text-primary">{c.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsPanel;
